import { Component, Input, OnDestroy, OnInit, TemplateRef, ViewChild } from '@angular/core';
import { AccordionComponent, MultiselectComponent, ButtonComponent, DatepickerComponent, DropdownSelectComponent } from '@ppl/ui';
import { NgZorroModule } from '../../../shared/modules/ng-zorro/ng-zorro.module';
import { NzDatePickerModule } from 'ng-zorro-antd/date-picker';
import { TableRowActionsModel } from '../../../shared/interfaces/list.interface';
import { TableRowActions } from '../../../shared/interfaces/actions';
import {
  FormArray,
  FormBuilder,
  FormControl,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
  Validators,
  AbstractControl,
  ValidatorFn,
  ValidationErrors
} from '@angular/forms';
import { CommonModule } from '@angular/common';
import { Program } from '../../../interfaces/user.interface';
import { BaseComponent } from '../../../shared/utils/base.component';
import { UserService } from '../../../services/user.service';
import { DateTimeFormatterService } from '../../../shared/utils/date-time-formatter.service';
import { EnrollmentDataService } from '../../../services/enrollment-data.service';
import { SectionsService } from '../../../services/sections.service';
import { ReferralDataService } from '../../../services/referral-data.service';
import { ActivatedRoute } from '@angular/router';
import { Subscription } from 'rxjs';
import { STATIC_DATA } from '../../../shared/constants/static-data.constants';
import { AuthorizationUtility } from '../../../shared/authorization/auth.utility';
import { MEMBER_VIEW_PAGE_IDS } from '../../../shared/constants/page.constants';
import { TableModule } from 'primeng/table';
import { ConsumerRatesPayload, ConsumerRatesResponse, MinimumWageRateResponse, ProviderServiceGridData, Service } from 'src/app/interfaces/consumer-rates.model';
import { ConsumerRateService } from 'src/app/services/consumer-rater.service.';
import { DialogConfig, DialogService } from 'src/app/shared/services/dialog.service';
import { ErrorAlertService } from 'src/app/shared/services/error-alert.service';
import { NotificationService } from '../../../shared/services/notification.service';
import { MessageModule } from 'primeng/message';
import { StorageService } from '../../../services/storage.service';

@Component({
  selector: 'ppl-consumer-rates',
  standalone: true,
  imports: [
    CommonModule,
    AccordionComponent,
    NgZorroModule,
    ReactiveFormsModule,
    FormsModule,
    NzDatePickerModule,
    ButtonComponent,
    DropdownSelectComponent,
    MultiselectComponent,
    DatepickerComponent,
    TableModule,
    MessageModule
  ],
  templateUrl: './consumer-rates.component.html',
  styleUrl: './consumer-rates.component.css',
})
export class ConsumerRatesComponent extends BaseComponent implements OnInit, OnDestroy {
  @Input() mode = 'read';
  @Input() type = 'R'; // R for registration, D for details page.
  @Input() showAccordion = true;
  @ViewChild('confirmationDialog') confirmationDialog!: TemplateRef<any>;
  @ViewChild('noSelectionDialog') noSelectionDialog!: TemplateRef<any>;
  @ViewChild('backdatedEndDateDialog') backdatedEndDateDialog!: TemplateRef<any>;
  gridData: ProviderServiceGridData[] = [];
  consumerRateForm!: FormGroup;
  selectedRate: any;
  selectedLead: any;
  providers: any;
  cols = [
    { field: 'providerPPLId', header: 'PPL Id', jsonpath: 'providerPPLId', isDisplayedAtHeader: 'y', isSearchable: 'n' },
    { field: 'providerName', header: 'Provider Name', jsonpath: 'providerName', isDisplayedAtHeader: 'y', isSearchable: 'n' },
    { field: 'code', header: 'Service Code', jsonpath: 'code', isDisplayedAtHeader: 'y', isSearchable: 'n' },
    { field: 'rate', header: 'Rate', jsonpath: 'rate', isDisplayedAtHeader: 'y', isSearchable: 'n' },
    { field: 'premiumRate', header: 'Premium Rate', jsonpath: 'premiumRate', isDisplayedAtHeader: 'y', isSearchable: 'n' },
    { field: 'fromDate', header: 'Start Date', jsonpath: 'fromdate', isDisplayedAtHeader: 'y', isSearchable: 'n' },
    { field: 'toDate', header: 'End Date', jsonpath: 'todate', isDisplayedAtHeader: 'y', isSearchable: 'n' },
    { field: 'actions', header: 'Actions', jsonpath: 'actions', isDisplayedAtHeader: 'y', isSearchable: 'n' }
  ];
  columns = [
    { field: 'selected', header: '', jsonpath: 'selected', isDisplayedAtHeader: 'n', isSearchable: 'n' },
    { field: 'serviceCode', header: 'Service Code', jsonpath: 'serviceCode', isDisplayedAtHeader: 'y', isSearchable: 'n' },
    { field: 'fromDate', header: 'Start Date', jsonpath: 'fromdate', isDisplayedAtHeader: 'y', isSearchable: 'n' },
    { field: 'toDate', header: 'End Date', jsonpath: 'todate', isDisplayedAtHeader: 'y', isSearchable: 'n' },
    { field: 'rate', header: 'Rate', jsonpath: 'rate', isDisplayedAtHeader: 'y', isSearchable: 'n' },
    { field: 'premiumRate', header: 'Premium Rate', jsonpath: 'premiumRate', isDisplayedAtHeader: 'y', isSearchable: 'n' },
    { field: 'actions', header: 'Actions', jsonpath: 'actions', isDisplayedAtHeader: 'y', isSearchable: 'n' }
  ];
  availableColumns = this.columns;
  serviceCodes: any[] = [];
  program: Program | undefined;
  actionButtons: TableRowActionsModel = new TableRowActions();
  minRateValue?: number;
  maxRateValue?: number;
  showEditButton = false;
  // IDs queued for deletion (collected after confirmation)
  /** Rates removed in this edit, sent as one batch when the form is saved. */
  /** Deleting a rate is a separate permission from editing one. */
  canDelete = false;
  /**
   * A delete committed while the edit form was open. Deletes now apply immediately, so the
   * list behind the form is stale from that point on and has to be refetched when it closes.
   */
  private ratesDeleted = false;
  private subs: Subscription[] = [];
  private providerValueSub?: Subscription;
  selectedProvider: any = null;
  isProviderView: boolean = false;
  // True when the logged-in lead is a service provider (IR/IC/VE/IF), not an Employer/Auth Rep;
  // drives loading rates from the provider-scoped API instead of the consumer-rates API.
  isServiceProviderView: boolean = false;
  // Raw active associations of the logged-in provider (each carries participantId + providerType),
  // used to determine the logged-in lead's role for the selected participant.
  activeParticipantAssociations: any[] = [];
  providerAssociations: any[] = [];
  participantId: number | string = '';
  providerId: number | string = '';
  hasParticipantAddress: boolean = true;
  // Start of today (midnight). Patched toDate values and getMaxEndDate are date-only
  // (midnight), so keeping the current time-of-day here would make a toDate of "today"
  // fall before minEndDate and the datepicker would drop the value instead of showing it.
  minEndDate: Date = new Date(new Date().setHours(0, 0, 0, 0));
  maxEndDates: Map<number, Date> = new Map();
  isDataLoaded: boolean = false;
  isAddressCheckDone: boolean = true;
  canEditRates: boolean = false;
  isSectionEditable = false;
  providerInfo: any;
  // ── Rate prohibition / Authorize ─────────────────────────────────────────
  // Rates come back with prohibitionFlag=true when they are locked pending authorization.
  // An authorized user ticks the affected service codes and clears them in bulk.
  // TODO: gate on the PPL Admin / I&A role once the role config is available; today this
  // follows the same permission as editing the section.
  canAuthorizeRates = false;
  selectedProhibitedRateIds = new Set<number>();
  isAuthorizing = false;
  constructor(
    private consumerRatesService: ConsumerRateService,
    private userService: UserService,
    private formBuilder: FormBuilder,
    private dateTimeFormatterService: DateTimeFormatterService,
    private enrollmentDataService: EnrollmentDataService,
    private sectionService: SectionsService,
    private referralDataService: ReferralDataService,
    private route: ActivatedRoute,
    private dialogSvc: DialogService,
    private errorAlertService: ErrorAlertService,
    private sectionsService: SectionsService,
    private authUtility: AuthorizationUtility,
    private storageService: StorageService,
    private notificationService: NotificationService,
  ) {
    super();
    this.initializeForm();
  }

  ngOnInit(): void {
    const pageId = MEMBER_VIEW_PAGE_IDS[this.userService.getFirstSegment()!];
    // consumerRates tab has no section-level config, so use isTabEditable which
    // falls back to page-level buttons/actions to distinguish view from full
    this.isSectionEditable = pageId ? this.authUtility.isTabEditable(pageId, 'consumerRates') : false;
    this.canDelete = pageId ? this.authUtility.isSectionDeletable(pageId, 'consumerRates', 'consumerRates') : false;

    // Independent Facilitator viewing a participant from the Associations tab gets Provider
    // Services read-only. The flag rides on router state (set by associations.component's
    // goToConsumer) and member-view preserves it across tab switches via { state: history.state }.
    if (history?.state?.['facilitatorView']) {
      this.isSectionEditable = false;
    }
    this.program = this.userService.getSelectedProgram();
    this.selectedLead = this.userService.getSelectedLead();
    const leadEntry = this.userService.getLeadEntry();
    const steps = this.sectionService.getSectionData(this.program?.program_code, '');
    this.secData = steps['consumer_rates'];
    // Determine if the view is for employer based on lead entry.
    this.isProviderView = this.userService.getLeadEntry() == 'providerEnrollment' || this.sectionsService.getFlow() == 'providerEnrollment';
    // setting participantId or providerId based on view type
    if (this.isProviderView) {
      this.providerId = this.selectedLead;
      // Only load the participant dropdown here. The provider list (and therefore the
      // rates grid) is built per selected participant in onParticipantChange, because
      // the logged-in lead may be an Employer/Auth Rep with no rates of their own.
      this.fetchParticipantsForProvider();
    }
    else {
      this.participantId = this.selectedLead;
    }

    this.showEditButton = true;
    // allow editing only if it is participant enrollment flow and consumer rates section, for provider enrollment flow rates are read only.
    if (leadEntry === 'participantEnrollment') {
      this.canEditRates = true;
      this.canAuthorizeRates = this.isSectionEditable && this.authUtility.isButtonVisible(pageId, 'AuthorizeRates');
    }
    else{
      // remove or hide actions column
      this.cols = this.cols.filter(col => col.field !== 'actions');
    }

    if (!this.isProviderView) {
      this.getParticiantAssociationRates();
    }

    this.setSubscriptions();
  }

  setSubscriptions() {
    const routeSub = this.route.data.subscribe({
      next: (data: any) => {
        this.type = data.type ?? 'R';
      }
    });
    this.subs.push(routeSub);

    if (!this.isProviderView) {
      const refSub = this.referralDataService.nextClicked.subscribe((val: any) => {
        if (val === STATIC_DATA.CONSUMER_RATES) {
          this.referralDataService.setStepValidation({ step: STATIC_DATA.CONSUMER_RATES, val: true });
        }
      });
      this.subs.push(refSub);
    }
  }

  fetchParticipantsForProvider() {
    if (!this.providerId) {
      return;
    }

    const participantId = this.storageService.getSelfEmployerParticipantId();
    this.enrollmentDataService.getAssociationsDetails(this.program?.program_code || '', this.providerId, false, false, participantId).subscribe((response: any) => {
      const data = response.responsedata;
      this.providerInfo = data?.basicInfo ?? {};
      // hide disassociated consumers whose association has already ended; a future end date means they're still active
      const activeAssociations = data?.participantAssociations?.filter((p: any) =>
        p.associationStatus !== 'Disassociated' ||
        (p.associationEndDate && new Date(p.associationEndDate) > new Date())
      ) ?? [];
      // Keep the raw active associations (they carry providerType) so we can tell, per selected
      // participant, whether the logged-in lead is an Employer/Auth Rep vs a service provider.
      this.activeParticipantAssociations = activeAssociations;
      const uniqueParticipants = Array.from(new Set(activeAssociations.map((p: any) => p.participantId)));
      this.providerAssociations = uniqueParticipants.map((pid: any) => {
        return activeAssociations.find((p: any) => p.participantId === pid);
      });

      this.providerAssociations.forEach((assoc: any) => {
        assoc.displayName = `${assoc.participantFirstName} ${assoc.participantLastName}`;
      });
    });
  }

  getParticiantAssociationRates() {
    this.enrollmentDataService
      .getAssociationsDetails(this.program?.program_code ?? '', this.participantId, true, false)
      .subscribe((data: any) => {
        var vendors = data?.responsedata?.VE?.filter((vendor: any) => vendor.associationStatus !== 'Disassociated').map((vendor: any) => ({
          lkpValueName: `${vendor.providerFirstName} ${vendor.providerLastName}`,
          lkpValueCode: vendor.providerId,
          pplProviderId: vendor.pplProviderId,
          providerType: 'VE'
        })) ?? [];

        var independentContractors = data?.responsedata?.IC?.filter((ic: any) => ic.associationStatus !== 'Disassociated').map((ic: any) => ({
          lkpValueName: `${ic.providerFirstName} ${ic.providerLastName}`,
          lkpValueCode: ic.providerId,
          pplProviderId: ic.pplProviderId,
          providerType: 'IC'
        })) ?? [];

        var independentProviders = data?.responsedata?.IR?.filter((provider: any) => provider.associationStatus !== 'Disassociated').map((provider: any) => ({
          lkpValueName: `${provider.providerFirstName} ${provider.providerLastName}`,
          lkpValueCode: provider.providerId,
          pplProviderId: provider.pplProviderId,
          providerType: 'IR'
        })) ?? [];

        var independentFacilitators = data?.responsedata?.IF?.filter((provider: any) => provider.associationStatus !== 'Disassociated').map((provider: any) => ({
          lkpValueName: `${provider.providerFirstName} ${provider.providerLastName}`,
          lkpValueCode: provider.providerId,
          pplProviderId: provider.pplProviderId,
          providerType: 'IF'
        })) ?? [];

        // De-duplicate by provider id: the same person can be associated under more than one type
        // (e.g. IR and IC), and loadConsumerRates matches rates on providerId alone — so a repeated
        // id would push the identical rate set into gridData twice and every row would appear twice.
        const associatedProviders = [...vendors, ...independentContractors, ...independentProviders, ...independentFacilitators];
        this.providers = [
          ...new Map(associatedProviders.map((provider: any) => [provider.lkpValueCode, provider])).values()
        ];

        // This path is used for participant view and for Employer/Auth Reps: the full provider
        // roster is loaded and all the participant's rates come from the consumer-rates API.
        this.isServiceProviderView = false;
        this.loadConsumerRates();
      });
  }

  onParticipantChange(event: any) {
    this.participantId = event;
    if (!this.participantId) {
      return;
    }

    // Decide the viewer's role for THIS participant from the associations we already fetched
    // (fetchParticipantsForProvider) — no extra call needed.
    const isArOrEmp = this.activeParticipantAssociations.some((a: any) =>
      String(a.participantId) === String(this.participantId) &&
      (a.providerType === 'EMP' || a.providerType === 'AR'));
    this.isServiceProviderView = this.isProviderView && !isArOrEmp;

    if (this.isServiceProviderView) {
      // Service provider: skip fetching the participant's full provider roster (which would pull
      // in other providers' info unnecessarily). Build the single own-provider grid entry from
      // the association we already have and load only their own rates.
      this.buildOwnProviderRatesFromAssociation();
    } else {
      // Employer / Auth Rep: needs the participant's full provider list and all their rates.
      this.getParticiantAssociationRates();
    }
  }

  // Builds a single grid entry for the logged-in service provider using the association data
  // already loaded, then loads only their rates from the provider-scoped API.
  private buildOwnProviderRatesFromAssociation() {
    const assoc = this.activeParticipantAssociations.find((a: any) =>
      String(a.participantId) === String(this.participantId));
    this.providers = assoc ? [{
      lkpValueName: `${this.providerInfo?.fname ?? ''} ${this.providerInfo?.lname ?? ''}`.trim(),
      lkpValueCode: this.providerId,
      pplProviderId: this.providerInfo.providerPplId ?? '',
      providerType: assoc.providerType ?? ''
    }] : [];
    this.loadConsumerRates();
  }

  loadConsumerRates() {
    if (!this.providers || this.providers.length === 0) {
      this.isDataLoaded = true;
      return;
    }
    this.gridData = [];
    this.clearAuthorizeSelection();
    // Service providers (not AR/EMP) load only their own rates from the provider-scoped API;
    // everyone else (participant view, or Employer/Auth Rep) loads the full participant rates.
    const rates$ = this.isServiceProviderView
      ? this.consumerRatesService.getProviderRatesList(this.program?.program_code || '', this.participantId, this.providerId)
      : this.consumerRatesService.getConsumerRatesList(this.program?.program_code || '', this.participantId);
    rates$.subscribe({
      next: (data: ConsumerRatesResponse) => {
        var rates = data?.responsedata ?? [];
        if (rates.length > 0) {
          if (this.isServiceProviderView) {
            // Provider-scoped API already returns only this provider's rates, so group them all
            // under the single own-provider entry without filtering by providerId (the rows may
            // not carry one).
            const provider: any = this.providers[0];
            this.gridData.push({
              providerId: this.providerInfo?.providerId,
              providerPPLId: this.providerInfo?.providerPPLId ?? '',
              providerName: this.providerInfo?.fname + ' ' + this.providerInfo?.lname,
              providerType: this.providerInfo?.providerType ?? '',
              serviceList: rates
            });
          } else {
            this.providers.forEach((provider: any) => {
              const providerRates = rates.filter((rate: any) => rate.providerId === provider.lkpValueCode);
              if (providerRates.length > 0) {
                const grdEntry = {
                  providerId: provider?.lkpValueCode ?? null,
                  providerPPLId: provider?.pplProviderId ?? '',
                  providerName: provider?.lkpValueName ?? '',
                  providerType: provider?.providerType ?? '',
                  serviceList: providerRates
                }
                this.gridData.push(grdEntry);
              }
            });
          }
        }

        this.isDataLoaded = true;
      },
      error: (err) => {
        console.warn('Failed to load consumer rates', err);
      }
    });
    this.consumerRateForm.reset({ participantId: this.participantId, providerId: '', code: '', serviceList: [] });
    this.mode = 'read';
    this.selectedRate = null;
  }

  fetchLookupValues() {
    if (this.providerValueSub) {
      this.providerValueSub.unsubscribe();
      this.providerValueSub = undefined;
    }
    const control = this.consumerRateForm.get('providerId');
    if (!control) {
      return;
    }
    this.providerValueSub = control.valueChanges.subscribe((value) => {
      if (value) {
        const newlySelectedProvider = this.providers.find((p: any) => p.lkpValueCode === value);

        if (this.selectedProvider?.lkpValueCode !== newlySelectedProvider?.lkpValueCode) {
          // clear existing service details and code selections if provider selection is changed to avoid mismatch between service details and provider.
          const codeControl = this.consumerRateForm.get('code');
          if (codeControl) {
            codeControl.setValue([]);
          }
          codeControl?.updateValueAndValidity();
          const serviceList = this.consumerRateForm.get('serviceList') as FormArray;
          serviceList.clear();
          serviceList.updateValueAndValidity();
        }

        this.selectedProvider = newlySelectedProvider;
        this.setAvailableColumns();
        // revalidate existing entries in serviceDetails FormArray
        this.loadConsumerServiceCodes();
      }
    });
    if (this.providerValueSub) {
      this.subs.push(this.providerValueSub);
    }
  }

  loadConsumerServiceCodes() {
    const programCode = this.program?.program_code ?? '';
    const payload = {
      participantID: this.participantId,
      providerID: this.consumerRateForm.get('providerId')?.value,
      type: this.type,
    };

    this.consumerRatesService.getProviderServiceCodes(programCode, payload).subscribe((data: any) => {
      if (data && data.length > 0) {
        // filter out servicecode_detail_id with value 187 and 188 are sicktime and unpaid leave service codes which should not be shown in service code dropdown for consumer rates section as these are only used for timesheet entry and not for rate management.
        this.serviceCodes = data.filter((sc: any) => !sc?.code?.includes('PRM') && sc?.servicecode_detail_id !== 187 && sc?.servicecode_detail_id !== 188);
        this.serviceCodes.forEach((sc: any) => {
          sc.label = `${sc.code} - ${sc.description}`;
        });
      }
    });
  }

  getMinimumRate(startDate?: string) {
    this.isAddressCheckDone = false;
    const dateStr = startDate ?? this.toApiDate(new Date());
    const sub = this.consumerRatesService.getMinimumRate(this.program?.program_code, this.participantId, dateStr)
      .subscribe({
        next: (data: MinimumWageRateResponse) => {
          if (!data || !data.responsedata) {
            this.isAddressCheckDone = true;
            return;
          }

          this.hasParticipantAddress = true;
          this.isAddressCheckDone = true;
          const d: any = data as any;
          const maxRate = d?.responsedata?.maxRate ?? 0;
          const rate = d?.responsedata?.rate ?? 0;
          this.minRateValue = +rate;
          this.maxRateValue = +maxRate;
        },
        error: (err: any) => {
          console.warn('Failed to load minimum rate', err?.error.responsedata);
          this.isAddressCheckDone = true;
          if (err?.error?.status === 406) {
            this.hasParticipantAddress = false;
            return;
          }
        }
      });
    this.subs.push(sub);
  }

  onClickAdd() {
    this.mode = 'add';
    // re-create form and then set up lookup subscription
    this.initializeForm();
    this.fetchLookupValues();
  }

  createRate() {
    this.consumerRateForm.markAllAsTouched();
    if (this.mode === 'add' && !this.hasParticipantAddress) {
      this.errorAlertService.openModal('error', 'Error', 'Participant does not have an address. Rate cannot be submitted.');
      return;
    }
    if (this.mode === 'add' && !this.consumerRateForm.valid) {
      return;
    }
    if (this.mode === 'add' && this.consumerRateForm.valid) {
      const formData = this.consumerRateForm.getRawValue();
      const payload = this.getCreateRatePayLoad(formData);
      this.consumerRatesService.createProviderServiceRates(this.program?.program_code || '', payload).subscribe((response) => {
        this.notifySaveResult(response);
        this.loadConsumerRates();
        this.mode = 'read';
      });
    } else {
      this.updateRate();
    }
  }

  updateRate() {
    // fetch only selected service details for update payload
    let selectedRates = this.serviceDetails.controls.filter(ctrl => ctrl.get('selected')?.value === true);
    if (selectedRates.length === 0) {
      // if no changes in service details, show confirmation to user before submitting update with existing service details, this is to handle the scenario where user might want to update provider selection without changing service details.
      this.openNoSelectionPopup();
      return;
    }

    // check selected service details for validation, if any selected service detail is invalid then show error and do not proceed with update
    const invalidSelected = selectedRates.some(ctrl => ctrl.invalid);
    if (invalidSelected) {
      return;
    }

    if (!invalidSelected) {
      const formData = this.consumerRateForm.getRawValue();
      const payload = this.getUpdateRatePayLoad(formData);
      if (this.selectedRate) {
        this.consumerRatesService.updateProviderServiceRates(this.program?.program_code || '', payload).subscribe((response) => {
          this.notifySaveResult(response);
          this.loadConsumerRates();
          this.mode = 'read';
        });
      }
    }
  }

  /**
   * Toaster for a rate create/update. A 200 can still carry an advisory in
   * responsedata.SVC_CODE_FLAG (e.g. "Regional center approval required for this association")
   * for one of the saved service codes — that takes precedence over the generic
   * responsemsg text, since it tells the user the rate is not usable yet.
   */
  private notifySaveResult(response: any): void {
    const svcCodeFlag = response?.responsedata?.SVC_CODE_FLAG;
    if (svcCodeFlag) {
      this.notificationService.alert('warning', 'Approval Required', svcCodeFlag);
      return;
    }

    const desc = response?.responsemsg?.[0]?.desc;
    if (desc) {
      this.notificationService.success('Success', desc);
    }
  }

  onEdit(data: ProviderServiceGridData, type: string) {
    if (type === 'edit') {
      this.prepareEdit(data);
    }
  }

  // ── Rate prohibition / Authorize ─────────────────────────────────────────

  /** A rate is selectable only while it is locked; already-authorized rates get no checkbox. */
  isRateProhibited(rate: Service): boolean {
    return rate?.prohibitionFlag === true && rate?.baseRateId != null;
  }

  /**
   * True when at least one loaded rate is locked. The per-rate lock icon in the grid is driven by
   * isRateProhibited and shown to every user; this only gates the authorizer-only controls.
   */
  get hasProhibitedRates(): boolean {
    return this.gridData.some(row => (row.serviceList ?? []).some(rate => this.isRateProhibited(rate)));
  }

  /** The checkbox column and Authorize button are only for users who can clear the flag. */
  get showAuthorizeColumn(): boolean {
    return this.canAuthorizeRates && this.hasProhibitedRates;
  }

  isRateSelected(rate: Service): boolean {
    return rate?.baseRateId != null && this.selectedProhibitedRateIds.has(rate.baseRateId);
  }

  onProhibitionSelectionChange(rate: Service, checked: boolean): void {
    if (rate?.baseRateId == null) {
      return;
    }
    if (checked) {
      this.selectedProhibitedRateIds.add(rate.baseRateId);
    } else {
      this.selectedProhibitedRateIds.delete(rate.baseRateId);
    }
  }

  clearAuthorizeSelection(): void {
    this.selectedProhibitedRateIds.clear();
  }

  /** Clears the prohibition flag on every ticked rate, then reloads so the grid reflects the unlock. */
  authorizeSelectedRates(): void {
    if (this.isAuthorizing || this.selectedProhibitedRateIds.size === 0) {
      return;
    }
    this.isAuthorizing = true;
    this.consumerRatesService
      .patchRateProhibitionFlag(this.program?.program_code, [...this.selectedProhibitedRateIds])
      .subscribe({
        next: () => {
          this.isAuthorizing = false;
          // loadConsumerRates clears the selection and re-reads the flags from the API.
          this.loadConsumerRates();
        },
        error: () => {
          // patchRateProhibitionFlag already surfaces the error through the global handler.
          this.isAuthorizing = false;
        }
      });
  }

  private prepareEdit(data: ProviderServiceGridData) {
    this.mode = 'update';
    this.selectedProvider = this.providers.find((p: any) => p.lkpValueCode === data.providerId);
    this.setAvailableColumns();
    // re-create form and then set up lookup subscription
    this.initializeForm();
    this.fetchLookupValues();
    this.selectedRate = data;

    const providerId = data.providerId ?? null;

    const selectedServiceCodes: number[] = Array.isArray(data.serviceList)
      ? data.serviceList
        .map((sd: any) => sd.servicecodeDetailId)
        .filter((v: any) => v != null)
      : [];

    this.consumerRateForm.patchValue({
      code: selectedServiceCodes,
      providerId: providerId
    });

    this.serviceDetails.clear();
    this.maxEndDates.clear();

    if (Array.isArray(data.serviceList) && data.serviceList.length > 0) {
      data.serviceList.forEach((sd: Service) => {
        const codeId: number | null = sd.servicecodeDetailId ?? null;
        const serviceCodeStr = sd.code ?? '';
        const grp = this.createServiceDetailsGroup(codeId, serviceCodeStr, false);
        grp.patchValue({
          selected: false,
          fromDate: sd.fromDate ?? null,
          toDate: sd.toDate ?? null,
          rate: sd.rate ?? null,
          premiumRate: sd.premiumRate ?? 0,
          baseRateId: sd.baseRateId ?? null,
          premiumRateId: sd.premiumRateId ?? null,
          prohibitionFlag: sd.prohibitionFlag === true
        });
        if (sd.toDate && sd.baseRateId != null) {
          this.maxEndDates.set(sd.baseRateId, new Date(sd.toDate));
        }
        grp.get('fromDate')?.disable();
        grp.get('rate')?.disable();
        grp.get('premiumRate')?.disable();
        // If record is not editable, disable the entire row. prohibitionFlag is an independent
        // lock: the rate stays read-only until an authorized user clears it from the rates grid.
        if ((sd as any).editable === 'N' || sd.prohibitionFlag === true) {
          grp.disable();
        }
        this.serviceDetails.push(grp);
      });
    }

    this.consumerRateForm.get('code')?.disable();
    this.consumerRateForm.get('providerId')?.disable();
    this.mode = 'update';
  }

  cancel() {
    this.mode = 'read';
    this.selectedRate = null;
    this.selectedProvider = null;
    this.maxEndDates.clear();
    this.initializeForm();

    // Closing after a delete: the list still shows the rate that has already gone.
    if (this.ratesDeleted) {
      this.refreshRatesList();
    }
  }

  /** Refetches the rates list and clears the pending-refresh flag. */
  private refreshRatesList() {
    this.ratesDeleted = false;
    this.loadConsumerRates();
  }

  getCreateRatePayLoad(formData: any): ConsumerRatesPayload {
    const serviceDetails = formData.serviceList ?? [];
    // if there is no toDate we need to keep 2999-12-3 as toDate in payload to avoid 
    // any issue with open end date in backend, this is to handle the scenario where user might want to add new service code with open end date by keeping end date empty in UI.
    // minRate, rate and PremumRate should be sent in payload for only independent provider.
    // we need to build based on selection of selected provider type because in case of add mode user can change provider selection after adding service details which can lead to mismatch in service details and provider type if we do not build payload based on selected provider at the time of create/update action.
    const services = serviceDetails.map((d: any) => {
      const svc: any = {
        servicecodeDetailId: +d.serviceCodeId,
        rate: +d.rate || 0,
        minRate: this.minRateValue,
        premiumRate: +d.premiumRate || 0,
        fromDate: this.dateTimeFormatterService.formatDateToString(new Date(d.fromDate)),
        toDate: this.dateTimeFormatterService.formatDateToString(new Date(d.toDate || '2999-12-31'))
      };
      if (this.mode === 'update' && d.baseRateId != null && d.premiumRateId != null) {
        svc.baseRateId = +d.baseRateId;
        svc.premiumRateId = +d.premiumRateId;
      }
      return svc;
    });

    return {
      participantId: +this.participantId,
      providerId: formData.providerId,
      serviceList: services
    };
  }

  getUpdateRatePayLoad(formData: any): ConsumerRatesPayload {
    const serviceDetails = formData.serviceList ?? [];
    const services = serviceDetails.filter((d: any) => d.selected === true).map((d: any) => {
      const svc: any = {
        servicecodeDetailId: +d.serviceCodeId,
        rate: +d.rate || 0,
        minRate: this.minRateValue,
        premiumRate: +d.premiumRate || 0,
        fromDate: this.formatRateDate(d.fromDate)
      };
      if (d.toDate) {
        svc.toDate = this.formatRateDate(d.toDate);
      }
      svc.baseRateId = +d.baseRateId;
      svc.premiumRateId = +d.premiumRateId;
      return svc;
    });

    return {
      participantId: +this.participantId,
      providerId: formData.providerId,
      serviceList: services
    };
  }

  initializeForm() {
    this.consumerRateForm = this.formBuilder.group({
      participantId: new FormControl(this.participantId),
      code: new FormControl([], [Validators.required]),
      providerId: new FormControl('', [Validators.required]),
      serviceList: this.formBuilder.array([]),
    });
  }


  get serviceDetails(): FormArray {
    return this.consumerRateForm.get('serviceList') as FormArray;
  }

  getServiceCodeLabel(serviceCodeId: any, fallbackCode: string): string {
    const match = this.serviceCodes.find(sc => sc.servicecode_detail_id === serviceCodeId);
    return match?.label ?? fallbackCode ?? '';
  }

  onServiceChange(selectedServiceCodes: number[]) {
    if (selectedServiceCodes.length > 0) {
      const currentDetails = this.serviceDetails;
      for (let i = currentDetails.length - 1; i >= 0; i--) {
        const serviceCodeId = currentDetails.at(i).get('serviceCodeId')?.value;
        if (!selectedServiceCodes.includes(serviceCodeId)) {
          currentDetails.removeAt(i);
        }
      }
      selectedServiceCodes.forEach(code => {
        const exist = currentDetails.controls.some(ctrl => ctrl.get('serviceCodeId')?.value === code);
        if (!exist) {
          const serviceCodeInfo = this.serviceCodes.find(sc => sc.servicecode_detail_id === code);
          var newGroup = this.createServiceDetailsGroup(code, serviceCodeInfo?.code || '', false);
          currentDetails.push(newGroup);
        }
      });
    } else {
      this.serviceDetails.clear();
    }
  }

  createServiceDetailsGroup(code: number | null, serviceCode: string, isNew = false): FormGroup {
    const decimalPattern = Validators.pattern(/^\d+(\.\d{1,2})?$/);
    const grp = this.formBuilder.group({
      selected: new FormControl(false),
      serviceCodeId: new FormControl(code),
      serviceCode: new FormControl(serviceCode),
      fromDate: new FormControl('', [Validators.required]),
      toDate: new FormControl(''),
      rate: new FormControl(this.minRateValue, [decimalPattern]),
      premiumRate: new FormControl('', [decimalPattern]),
      isNew: new FormControl(isNew),
      baseRateId: new FormControl(null),
      premiumRateId: new FormControl(null),
      // Display-only: lets the edit table explain why a disabled row cannot be changed. Not read
      // by getCreateRatePayLoad / getUpdateRatePayLoad, so it never reaches the API.
      prohibitionFlag: new FormControl(false)
    },
      { validators: [this.dateRangeValidator] });
    if (serviceCode === '338') {
      // Leave the rate empty until a start date is selected; then fetch the latest mileage rate.
      grp.get('rate')?.setValue(null);
      grp.get('fromDate')?.valueChanges.subscribe(() => this.applyMileageRate(grp));
    }

    // Invoice service codes — rates are not applicable
    const invoiceServiceCodes = [
      '313-INV', '320-INS', '320-INV', '331-INV', '333-INV',
      '334-INV', '335-INV', '337-INV', '338-INV', '339-INV', '360-INV'
    ];
    const isInvoiceCode = invoiceServiceCodes.includes(serviceCode);

    if (isInvoiceCode) {
      grp.get('rate')?.setValue(null);
    }

    if (this.selectedProvider?.providerType !== 'IR' || serviceCode === '338' || isInvoiceCode) {
      grp.get('rate')?.disable();
      grp.get('premiumRate')?.disable();
    }
    if (this.selectedProvider?.providerType === 'IR' && serviceCode !== '338' && !isInvoiceCode) {
      grp.addValidators(this.premiumRateValidator());
      grp.get('rate')?.addValidators([Validators.required, this.minRateValidator()]);

      grp.get('fromDate')?.valueChanges.subscribe((dateVal: any) => {
        if (dateVal && this.mode === 'add') {
          const dateStr = this.toApiDate(new Date(dateVal));
          this.consumerRatesService.getMinimumRate(this.program?.program_code, this.participantId, dateStr)
            .subscribe({
              next: (data: any) => {
                this.hasParticipantAddress = true;
                this.minRateValue = +(data?.responsedata?.rate ?? 0);
                this.maxRateValue = +(data?.responsedata?.maxRate ?? 0);
                const rateCtrl = grp.get('rate');
                if (rateCtrl) {
                  rateCtrl.setValidators([Validators.required, Validators.pattern(/^\d+(\.\d{1,2})?$/), this.minRateValidator()]);
                  rateCtrl.setValue(this.minRateValue);
                  rateCtrl.updateValueAndValidity();
                }
              },
              error: (err: any) => {
                if (err?.error?.status === 406) {
                  this.hasParticipantAddress = false;
                }
              }
            });
        }
      });
    }

    // Subscribe to valueChanges to trigger FormArray validation dynamically
    grp.valueChanges.subscribe((val) => {
      const serviceList = this.consumerRateForm.get('serviceList') as FormArray;
      serviceList.updateValueAndValidity({ onlySelf: true, emitEvent: false });
    });

    return grp;
  }

  private minRateValidator(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      if (this.minRateValue == null || this.minRateValue === 0) return null;
      const val = parseFloat(control.value);
      if (isNaN(val)) return null;
      return val >= this.minRateValue ? null : { min: { min: this.minRateValue, actual: val } };
    };
  }

  private premiumRateValidator(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      const grp = control as FormGroup;
      const rateVal = grp.get('rate')?.value;
      const premiumVal = grp.get('premiumRate')?.value;
      if (rateVal === null || rateVal === undefined || premiumVal === null || premiumVal === undefined) {
        return null;
      }
      const rateNum = +rateVal;
      const premNum = +premiumVal;
      if (!Number.isFinite(rateNum) || !Number.isFinite(premNum)) { return { premiumInvalid: true }; }
      if (premNum === 0) { return null; }
      return null;
    };
  }

  dateRangeValidator: ValidatorFn = (
    group: AbstractControl
  ): ValidationErrors | null => {

    const from = group.get('fromDate')?.value;
    const to = group.get('toDate')?.value;

    if (!from || !to) return null;

    return new Date(to) >= new Date(from)
      ? null
      : { dateRangeInvalid: true };
  };

  private formatRateDate(value: string | Date | null | undefined): string {
    return value ? this.dateTimeFormatterService.formatDateToString(new Date(value)) : '';
  }

  private toApiDate(date: Date): string {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }

  // Fallback mileage rate used when the rate API request fails or returns no usable value.
  private readonly MILEAGE_RATE_FALLBACK = 0.725;

  // Fetch the latest mileage rate for a service-details group using its start date (fromDate).
  // Falls back to MILEAGE_RATE_FALLBACK when the date is missing or the API fails/returns nothing.
  private applyMileageRate(grp: FormGroup): void {
    const rateCtrl = grp.get('rate');
    const fromDate = grp.get('fromDate')?.value;
    if (!fromDate) {
      // No start date yet — keep the rate empty rather than showing the fallback.
      rateCtrl?.setValue(null);
      return;
    }
    const serviceCodeDetailId = grp.get('serviceCodeId')?.value;
    const dateStr = this.toApiDate(new Date(fromDate));
    this.consumerRatesService.getServiceCodeRate(this.program?.program_code, serviceCodeDetailId, dateStr).subscribe({
      next: (data: any) => {
        const rate = Number(data?.responsedata?.rate ?? data?.responsedata);
        rateCtrl?.setValue(Number.isFinite(rate) && rate > 0 ? rate : this.MILEAGE_RATE_FALLBACK);
      },
      error: () => rateCtrl?.setValue(this.MILEAGE_RATE_FALLBACK)
    });
  }


  duplicateRow(i: number) {
    const currentDetails = this.serviceDetails;
    const src = currentDetails.at(i) as FormGroup;
    const code = src.get('serviceCodeId')?.value;
    const serviceCode = src.get('serviceCode')?.value;
    const fromDate = src.get('fromDate')?.value;
    const toDate = src.get('toDate')?.value;
    const rate = src.get('rate')?.value;

    const newGroup = this.createServiceDetailsGroup(code, serviceCode, true);
    newGroup.patchValue({ fromDate, toDate, rate, premiumRate: rate });
    currentDetails.insert(i + 1, newGroup);
  }

  deleteRow(i: number) {
    const row = this.serviceDetails.at(i);
    const isSavedRate = this.mode === 'update' && !row?.get('isNew')?.value;

    // An unsaved row exists only in the form, so it just goes. A saved rate has to be
    // confirmed first — previously the row was removed either way, even on cancel.
    if (!isSavedRate) {
      this.serviceDetails.removeAt(i);
      return;
    }

    this.openConfirmationPopup(row?.value, i);
  }

  addRow(i: number) {
    const currentDetails = this.serviceDetails;
    const src = currentDetails.at(i) as FormGroup;
    const code = src.get('serviceCodeId')?.value;
    const serviceCode = src.get('serviceCode')?.value;
    currentDetails.insert(i + 1, this.createServiceDetailsGroup(code, serviceCode, true));
  }

  showAddButton(index: number): boolean {
    const row = this.serviceDetails.at(index);

    // Unchanged in effect: while the actions column was dropped in update mode this button was
    // unreachable there, so keeping it out of update mode adds no new capability.
    return this.mode !== 'update' && !(row?.get('isNew')?.value === true);
  }

  showDeleteButton(index: number): boolean {
    const row = this.serviceDetails.at(index);

    // A row the user just added is theirs to remove; deleting a saved rate needs permission.
    if (row?.get('isNew')?.value === true) {
      return true;
    }

    // Deliberately not gated on type: 'R' any more. That only ever matched the enrollment
    // wizard route — every lead-details route passes type: 'D' — so it blocked delete on the
    // detail page entirely. canDelete is the permission that governs it now.
    return this.canDelete && this.mode === 'update';
  }

  openConfirmationPopup(data: any, index: number) {
    const dialogConfig: DialogConfig = {
      title: 'Confirmation',
      okText: 'Remove',
      size: 'medium',
      position: 'top',
      showClose: true,
      showButtons: true
    };

    this.dialogSvc.openDialog(
      this.confirmationDialog,
      dialogConfig,
      () => this.confirmDelete(data, index),
      () => this.handleCancel()
    );
  }

  getMaxEndDate(details: any): Date | null {
    if (this.mode !== 'update') return null;
    const baseRateId = details.get('baseRateId')?.value;
    return baseRateId != null ? (this.maxEndDates.get(baseRateId) ?? null) : null;
  }

  openBackdatedEndDatePopup() {
    const dialogConfig: DialogConfig = {
      title: 'Error',
      okText: 'OK',
      size: 'medium',
      position: 'top',
      showClose: true,
      showButtons: false,
      showOkButton: true
    };

    this.dialogSvc.openDialog(
      this.backdatedEndDateDialog,
      dialogConfig,
      () => { },
      () => { }
    );
  }

  openNoSelectionPopup() {
    const dialogConfig: DialogConfig = {
      title: 'Error',
      okText: 'OK',
      size: 'medium',
      position: 'top',
      showClose: true,
      showButtons: false,
      showOkButton: true
    };

    this.dialogSvc.openDialog(
      this.noSelectionDialog,
      dialogConfig,
      () => { },
      () => { }
    );
  }

  /** Queues the rate for deletion and drops its row now that the user has confirmed. */
  /**
   * Deletes the rate as soon as the user confirms, rather than queueing it for the next save.
   * A saved rate is identified by its baseRateId / premiumRateId pair — the same pair the save
   * payload sends; the row has no `id` control.
   */
  private confirmDelete(data: any, index: number) {
    const baseRateId = data?.baseRateId ?? null;

    // Nothing persisted for this row, so it only has to leave the form.
    if (baseRateId == null) {
      this.serviceDetails.removeAt(index);
      return;
    }

    const premiumRateId = data?.premiumRateId != null ? +data.premiumRateId : '';

    this.consumerRatesService
      .deleteProviderRates(this.program?.program_code, [+baseRateId], [premiumRateId])
      .subscribe({
        next: (response: any) => {
          this.notifyDeleteSuccess(response);
          this.ratesDeleted = true;
          // Dropped only once the server has accepted it, so a failure leaves the row in place.
          this.serviceDetails.removeAt(index);

          // The last rate has gone, so there is nothing left to edit — back to the refreshed list.
          if (this.serviceDetails.length === 0) {
            this.refreshRatesList();
            this.mode = 'read';
          }
        },
        error: (err) => {
          console.warn('Error deleting rate', err);
        }
      });
  }


  /**
   * Success toaster for a delete. responsemsg comes back as a bare object on some endpoints
   * and an array on others, so both shapes are read before falling back to a plain message.
   */
  private notifyDeleteSuccess(data: any) {
    const responseMessage = data?.responsemsg;
    const description = Array.isArray(responseMessage)
      ? responseMessage[0]?.desc
      : responseMessage?.desc;

    this.notificationService.success('Success', description || 'Rate deleted successfully.');
  }

  handleCancel() { }

  setAvailableColumns() {
    // if selectedProvider is not IP, we need to hide rate and premium rate columns
    if (this.selectedProvider && this.selectedProvider.providerType !== 'IR') {
      this.availableColumns = this.columns.filter(col => col.field !== 'rate' && col.field !== 'premiumRate');
    } else {
      this.availableColumns = this.columns;
    }

    // The actions column is kept in every mode — showAddButton / showDeleteButton decide what
    // appears in it. It used to be dropped in update mode, which left the Delete button
    // unreachable, since that button also requires update mode.
    if (this.mode !== 'update') {
      // Selection is not required outside update mode, and dropping it keeps actions last.
      this.availableColumns = this.availableColumns.filter(col => col.field !== 'selected');
    }
  }

  ngOnDestroy() {
    this.subs.forEach(s => {
      try { s.unsubscribe(); } catch { /* ignore */ }
    });
    this.subs = [];
  }
}
